import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';

import { Icon as KitIcon } from '@column/column-ui-kit';
import { Button, Icon } from '@column/ui';

import { SetupMfaTotpModal } from '~/app/pages/User/Personal/SetupTotpModal';
import { useDialog } from '~/components/Dialog/Provider';
import { FactorTypeResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

export const SectionMfaTotp = () => {
  const { openAlert } = useDialog();
  const { currentUser, refetchCurrentUser } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();

  const [setupMfaTotpModalOpen, setSetupMfaTotpModalOpen] = useState<boolean>(false);
  const closeSetupMfaTotpModal = useCallback(() => setSetupMfaTotpModalOpen(false), []);

  const totpFactor = useMemo(
    () => currentUser?.factors.find((factor) => factor.factorType === FactorTypeResponse.TOTP && factor.factorVerified),
    [currentUser]
  );

  const deleteTotpFactorMutation = useMutation({
    mutationFn: ({ factorId }: { factorId: string }) => UserRepository.deleteFactor({ factorId }),
    onSuccess: () => {
      addSuccessNotification({ content: 'Authenticator app disabled' });
      refetchCurrentUser();
    },
    onError: (error: unknown) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to disable the authenticator app',
      });
    },
  });

  const handleDisableTotpMfa = () => {
    const secondaryText = currentUser?.factors.find((factor) => factor.factorType === FactorTypeResponse.SMS)
      ? 'If authenticator app two-factor authentication is disabled, your phone number will be used instead.'
      : 'If two-factor authentication is disabled, your account will be less secure.';

    openAlert({
      headline: 'Disable Authenticator App',
      content: `This action cannot be undone. ${secondaryText}`,
      confirmButton: {
        label: 'Disable',
        variant: 'danger',
      },
      onSuccess: () => {
        if (totpFactor) {
          deleteTotpFactorMutation.mutate({ factorId: totpFactor.factorId });
        }
      },
    });
  };

  return (
    <>
      <SetupMfaTotpModal
        onClose={closeSetupMfaTotpModal}
        onSuccess={() => {
          closeSetupMfaTotpModal();
          refetchCurrentUser();
        }}
        open={setupMfaTotpModalOpen}
      />

      <div className="flex items-center gap-4 rounded-lg bg-gray-25 px-4 py-3 ring-1 ring-black/[0.08] dark:bg-white/[0.02] dark:ring-white/[0.12]">
        <div className="flex flex-auto items-center gap-2 text-sm leading-5 text-muted-foreground">
          {totpFactor ? (
            <KitIcon.Checkmark className="shrink-0 text-seafoam-600 [--icon-size-x:24px] [--icon-size-y:24px] dark:text-seafoam-400" />
          ) : (
            <Icon.QRCode />
          )}
          <span>
            {totpFactor
              ? 'Your authenticator app is enabled.'
              : 'Generate one-time verification codes with your preferred authenticator app.'}
          </span>
        </div>

        {totpFactor ? (
          <Button
            type="button"
            size="sm"
            variant="muted"
            disabled={deleteTotpFactorMutation.isPending}
            onClick={handleDisableTotpMfa}
          >
            Disable
          </Button>
        ) : (
          <Button type="button" size="sm" variant="secondary" onClick={() => setSetupMfaTotpModalOpen(true)}>
            Enable
          </Button>
        )}
      </div>
    </>
  );
};
